import React, { useState, useEffect } from 'react';
import { Search, Plus, X, MessageCircle, Megaphone, ExternalLink, Circle, Paperclip, Image, FileText, Music, Film, Mic } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { useChatPopup } from './ChatPopupManager';
import { useMessagesQuery } from '@/hooks/use-messages-query';
import { messagingApi, type Conversation } from '@/api/messaging';
import { broadcastsApi } from '@/api/broadcasts';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getUserEntityId } from '@/lib/chat-utils';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useNavigate } from 'react-router-dom';
import { NewChatModal } from './NewChatModal';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';

// Helper function to format message preview
const formatMessagePreview = (lastMessage: Conversation['last_message']) => {
  if (!lastMessage) return 'No messages yet';

  // Check for voice notes - either by message_type or by checking first attachment
  if (lastMessage.message_type === 'voice' ||
      (lastMessage.first_attachment?.file_type === 'voice_note')) {
    return 'Voice note';
  }

  // For other media/attachments
  if (lastMessage.has_attachments || lastMessage.message_type === 'media') {
    // If there's content with attachments, show the content
    if (lastMessage.content) {
      return lastMessage.content;
    }
    // Otherwise show attachment count
    return `${lastMessage.attachment_count || 1} attachment${lastMessage.attachment_count === 1 ? '' : 's'}`;
  }

  return lastMessage.content || 'No messages yet';
};

interface ChatListDropdownProps {
  userRole?: 'client' | 'filmmaker' | 'admin';
}

export const ChatListDropdown: React.FC<ChatListDropdownProps> = ({ 
  userRole = 'filmmaker' 
}) => {
  const { openChat, toggleChatList, openChats, currentUserRole } = useChatPopup();
  const { userType: authUserType } = useAuth();
  // Dual users get the unified-search 'both' mode in the new chat modal,
  // regardless of which dashboard route they're currently viewing.
  const newChatModalUserRole: 'client' | 'filmmaker' | 'both' | 'admin' =
    authUserType === 'both' ? 'both' : (currentUserRole || 'filmmaker');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentEntityId, setCurrentEntityId] = useState<string | null>(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [, forceUpdate] = useState(0); // Force re-render trigger
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  
  // Use the role from context if available, otherwise fall back to prop
  const effectiveUserRole = currentUserRole || userRole;

  // Get current user entity ID - cached with React Query for instant availability
  const { data: cachedEntityId } = useQuery({
    queryKey: ['entity-id-dropdown', effectiveUserRole],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      return getUserEntityId(user.id, effectiveUserRole as 'client' | 'filmmaker' | 'admin');
    },
    enabled: !!effectiveUserRole,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Update state when cached entity ID is available
  useEffect(() => {
    if (cachedEntityId && cachedEntityId !== currentEntityId) {
      setCurrentEntityId(cachedEntityId);
    }
  }, [cachedEntityId]);

  // Fetch conversations using getConversationsWithState for consistent participant names
  const { data: conversations, isLoading, refetch } = useQuery<Conversation[]>({
    queryKey: ['conversations', currentEntityId, effectiveUserRole],
    queryFn: async () => {
      if (!currentEntityId) return [];
      return messagingApi.getConversationsWithState(currentEntityId, effectiveUserRole as 'client' | 'filmmaker');
    },
    enabled: !!currentEntityId && !!effectiveUserRole,
    staleTime: 0, // Always consider data stale to ensure fresh data
    gcTime: 1000, // Keep cache for 1 second only (gcTime is the new name for cacheTime in v5)
    refetchInterval: 10000, // 10 seconds
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  
  // Listen for message sent events to refresh conversations
  useEffect(() => {
    const handleMessageSent = () => {
      refetch();
    };
    
    const handleNewChat = () => {
      // Small delay to allow the new chat to be created in the database
      setTimeout(() => {
        refetch();
      }, 500);
    };
    
    window.addEventListener('message-sent', handleMessageSent);
    window.addEventListener('messages-read', handleMessageSent);
    window.addEventListener('new-chat-created', handleNewChat);
    
    return () => {
      window.removeEventListener('message-sent', handleMessageSent);
      window.removeEventListener('messages-read', handleMessageSent);
      window.removeEventListener('new-chat-created', handleNewChat);
    };
  }, [refetch]);
  
  // Refetch when entity ID changes (ensures fresh data)
  useEffect(() => {
    if (currentEntityId) {
      refetch();
    }
  }, [currentEntityId, refetch]);

  // Fetch broadcasts for unread count
  const { data: broadcasts } = useQuery({
    queryKey: ['user-broadcasts', effectiveUserRole],
    queryFn: () => broadcastsApi.getMyBroadcasts(effectiveUserRole),
    enabled: !!effectiveUserRole,
    staleTime: 60 * 1000,
  });

  // Get viewed broadcast IDs from localStorage and calculate unread count
  const viewedBroadcasts = JSON.parse(localStorage.getItem('viewedBroadcasts') || '[]');
  const unreadBroadcastCount = broadcasts?.filter(b => 
    b.is_active && !viewedBroadcasts.includes(b.id)
  ).length || 0;

  const filteredConversations = conversations?.filter(conv => {
    // Handle group chats (use group_name) or direct chats (use participant_name)
    const displayName = conv.room_type === 'group'
      ? (conv.group_name || 'Group Chat')
      : (conv.participant_name || 'Unknown');
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  const handleSelectChat = (conv: Conversation) => {
    // Handle group vs direct chat names
    const chatName = conv.room_type === 'group'
      ? (conv.group_name || 'Group Chat')
      : (conv.participant_name || 'Unknown');
    const chatAvatar = conv.room_type === 'group'
      ? conv.group_avatar
      : conv.participant_avatar;

    openChat({
      id: conv.room_name,
      name: chatName,
      avatar: chatAvatar,
      role: conv.participant_role,
      isMinimized: false,
      unreadCount: conv.unread_count,
    });
    
    // Always close dropdown after selecting a chat
    toggleChatList();
  };

  const handleBroadcastClick = () => {
    // Mark all active broadcasts as viewed
    if (broadcasts) {
      const viewedBroadcasts = JSON.parse(localStorage.getItem('viewedBroadcasts') || '[]');
      const activeBroadcastIds = broadcasts
        .filter(b => b.is_active)
        .map(b => b.id);
      
      // Add any new broadcasts to viewed list
      const newViewedBroadcasts = [...new Set([...viewedBroadcasts, ...activeBroadcastIds])];
      localStorage.setItem('viewedBroadcasts', JSON.stringify(newViewedBroadcasts));
      
      // Force re-render to update the count
      forceUpdate(prev => prev + 1);
      
      // Dispatch a custom event to notify other components
      window.dispatchEvent(new CustomEvent('broadcasts-viewed', { 
        detail: { viewedIds: activeBroadcastIds } 
      }));
    }
    
    // Open as a regular chat
    openChat({
      id: 'broadcast',
      name: 'Find My Filmmaker',
      avatar: undefined,
      role: 'client',
      isMinimized: false,
      unreadCount: 0, // Set to 0 since we just marked as viewed
    });
    
    // Always close dropdown after selecting
    toggleChatList();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b flex-shrink-0">
        {/* Mobile drag handle */}
        {isMobile && (
          <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
        )}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Chats</h2>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNewChatModal(true)}
              className="h-8 w-8"
              title="Start new chat"
            >
              <Plus className="h-4 w-4" />
            </Button>
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleChatList}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <motion.div 
          className="p-2 space-y-2 max-w-full"
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.05
              }
            }
          }}
        >
          {/* Broadcast Message (if not admin) */}
          {effectiveUserRole !== 'admin' && (
            <motion.div
              variants={{
                hidden: { y: 20, opacity: 0 },
                visible: { 
                  y: 0, 
                  opacity: 1,
                  transition: {
                    type: "spring",
                    damping: 25,
                    stiffness: 300
                  }
                }
              }}
              className="group flex items-center gap-3 p-3 rounded-lg hover:bg-primary/10 cursor-pointer bg-gradient-to-r from-primary/5 to-transparent border border-primary/20 outline-none focus:outline-none focus:ring-0 focus:bg-primary/8 transition-all overflow-hidden"
              onClick={handleBroadcastClick}
              tabIndex={0}
              whileHover={{ scale: 1.01 }}
            >
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center shadow-md">
                <Megaphone className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <div className="font-medium flex items-center gap-2">
                  Find My Filmmaker
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">Official</Badge>
                </div>
                <div className="text-sm text-muted-foreground">Announcements & Updates</div>
              </div>
              {unreadBroadcastCount > 0 && (
                <Badge variant="destructive" className="h-5 px-1">
                  {unreadBroadcastCount}
                </Badge>
              )}
            </motion.div>
          )}

          {/* Separator */}
          {effectiveUserRole !== 'admin' && filteredConversations.length > 0 && (
            <motion.div
              variants={{
                hidden: { opacity: 0, scaleX: 0 },
                visible: { 
                  opacity: 1, 
                  scaleX: 1,
                  transition: {
                    type: "spring",
                    damping: 25,
                    stiffness: 300,
                    delay: 0.05
                  }
                }
              }}
              className="my-3 px-3"
            >
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Direct Messages</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            </motion.div>
          )}

          {/* Conversations - Show all but with scroll */}
          {isLoading ? (
            <motion.div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-3 p-3"
                >
                  <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                    <div className="h-3 bg-muted rounded w-48 animate-pulse" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : filteredConversations.length > 0 ? (
            <AnimatePresence>
              {filteredConversations.map((conv: Conversation, index: number) => {
                const isOpen = openChats.some(chat => chat.id === conv.room_name);
                
                return (
                  <motion.div
                    key={conv.room_name}
                    variants={{
                      hidden: { x: -20, opacity: 0 },
                      visible: { 
                        x: 0, 
                        opacity: 1,
                        transition: {
                          type: "spring",
                          damping: 20,
                          stiffness: 300,
                          delay: index * 0.03
                        }
                      }
                    }}
                    initial="hidden"
                    animate="visible"
                    exit={{ x: 20, opacity: 0 }}
                    className={cn(
                      "group flex items-center gap-3 p-3 rounded-lg cursor-pointer outline-none focus:outline-none focus:ring-0 focus:bg-accent/30 transition-all max-w-full",
                      isOpen ? "bg-accent/40 hover:bg-accent/50" : "hover:bg-accent/30"
                    )}
                    onClick={() => handleSelectChat(conv)}
                    tabIndex={0}
                    whileHover={{ scale: 1.01 }}
                  >
                  <Avatar className="h-10 w-10">
                    {(conv.room_type === 'group' ? conv.group_avatar : conv.participant_avatar) ? (
                      <AvatarImage src={conv.room_type === 'group' ? conv.group_avatar : conv.participant_avatar} />
                    ) : (
                      <AvatarFallback>
                        {(conv.room_type === 'group'
                          ? (conv.group_name || 'GC')
                          : (conv.participant_name || 'U')
                        ).slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">
                        {conv.room_type === 'group'
                          ? (conv.group_name || 'Group Chat')
                          : (conv.participant_name || 'Unknown')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                        {(conv.last_message?.message_type === 'voice' ||
                          conv.last_message?.first_attachment?.file_type === 'voice_note') ? (
                          <Mic className="h-3.5 w-3.5 mr-1 shrink-0 text-muted-foreground" />
                        ) : conv.last_message?.has_attachments ? (
                          <Paperclip className="h-3.5 w-3.5 mr-1 shrink-0 text-muted-foreground" />
                        ) : null}
                        <span>{formatMessagePreview(conv.last_message)}</span>
                      </p>

                      {conv.last_message?.created_at && (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDistanceToNow(new Date(conv.last_message.created_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {conv.unread_count > 0 && (
                      <>
                        <Circle className="h-2 w-2 fill-primary text-primary" />
                        <Badge variant="destructive" className="h-5 px-1">
                          {conv.unread_count}
                        </Badge>
                      </>
                    )}
                  </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No conversations found' : 'No conversations yet'}
            </div>
          )}
          
        </motion.div>
      </div>

      {/* Footer with Open in Messenger link */}
      <div className="p-3 border-t bg-muted/50">
        {!isMobile && openChats.length > 0 && (
          <div className="text-xs text-muted-foreground mb-2">
            {openChats.length} active {openChats.length === 1 ? 'chat' : 'chats'}
          </div>
        )}
        {/* Hide Open in Messages button on mobile */}
        {!isMobile && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start hover:bg-accent/50 transition-colors"
            onClick={() => {
              const messagesPath = effectiveUserRole === 'client'
                ? '/client/dashboard/messages'
                : effectiveUserRole === 'admin'
                ? '/admin/dashboard/messages'
                : '/filmmaker/dashboard/messages';
              navigate(messagesPath);
              toggleChatList();
            }}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            <span className="text-primary">Open in Messages</span>
          </Button>
        )}
      </div>
      
      {/* New Chat Modal — dual users get 'both' so they can message filmmakers + clients */}
      <NewChatModal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        userRole={newChatModalUserRole}
      />
    </div>
  );
};